// src/pages/Legal/FAQPage.jsx

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  InputBase,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  Assignment as AssignmentIcon,
  QuestionAnswer as QuestionAnswerIcon,
  DateRange as DateRangeIcon,
  AccountBox as AccountBoxIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Categorias de FAQ
  const categories = [
    { id: 'all', label: 'Todas', icon: QuestionAnswerIcon },
    { id: 'inscricao', label: 'Pré-Inscrições', icon: AssignmentIcon },
    { id: 'pagamento', label: 'Pagamentos', icon: PaymentIcon },
    { id: 'estudante', label: 'Estudantes', icon: PersonIcon },
    { id: 'instituicao', label: 'Instituições', icon: SchoolIcon },
    { id: 'campanha', label: 'Campanhas', icon: CampaignIcon },
    { id: 'conta', label: 'Conta', icon: AccountBoxIcon },
  ];

  // Perguntas frequentes
  const faqs = [
    {
      category: 'inscricao',
      question: 'Como faço uma pré-inscrição?',
      answer: `Para fazer uma pré-inscrição, siga estes passos:
        1. Faça login na sua conta de estudante
        2. Acesse a seção "Campanhas Disponíveis"
        3. Escolha a instituição e o curso desejado
        4. Clique em "Pré-inscrever-se"
        5. Preencha todos os dados necessários
        6. Envie os documentos solicitados
        7. Efetue o pagamento da taxa (se aplicável)`,
    },
    {
      category: 'pagamento',
      question: 'Quais são as formas de pagamento aceitas?',
      answer: 'Aceitamos diversas formas de pagamento, incluindo transferência bancária, M-PESA,E-Mola e outros métodos digitais. As opções específicas são definidas por cada instituição.',
    },
    {
      category: 'estudante',
      question: 'Como acompanho o status da minha pré-inscrição?',
      answer: 'Você pode acompanhar o status da sua pré-inscrição através do seu Dashboard do Estudante, na seção "Minhas Pré-inscrições". Lá você encontrará todas as informações sobre suas pré-inscrições em andamento.',
    },
    {
      category: 'instituicao',
      question: 'Como uma instituição pode se cadastrar?',
      answer: 'Instituições interessadas devem entrar em contacto com nossa equipe administrativa através do formulário de contacto ou pelo email institucional@zenkroll.co.mz para iniciar o processo de cadastro.',
    },
    {
      category: 'campanha',
      question: 'Qual é o prazo para me inscrever em uma campanha?',
      answer: 'Cada campanha tem seu próprio prazo, que está claramente indicado na página da campanha. Recomendamos ficar atento às datas e fazer a pré-inscrição com antecedência.',
    },
    {
      category: 'conta',
      question: 'Como altero minha senha?',
      answer: 'Para alterar sua senha, acesse seu perfil no menu superior direito, clique em "Editar Perfil" e depois em "Alterar Senha". Siga as instruções para criar uma nova senha segura.',
    },
    {
      category: 'inscricao',
      question: 'O que fazer se minha pré-inscrição for rejeitada?',
      answer: 'Se sua pré-inscrição for rejeitada, você receberá uma notificação com o motivo. Você pode corrigir as informações ou documentos necessários e submeter uma nova pré-inscrição, se o prazo da campanha ainda estiver vigente.',
    },
    {
      category: 'pagamento',
      question: 'Como emito o comprovante de pagamento?',
      answer: 'Após a confirmação do pagamento, acesse seu Dashboard, vá até a secção de pagamentos e clique em "Gerar Comprovativo" ao lado do pagamento desejado.',
    },
    {
      category: 'estudante',
      question: 'Posso me inscrever em múltiplas campanhas?',
      answer: 'Sim, você pode se inscrever em quantas campanhas desejar, desde que atenda aos requisitos específicos de cada uma e efetue os pagamentos necessários.',
    },
    {
      category: 'campanha',
      question: 'Como sou notificado sobre novas campanhas?',
      answer: 'Você receberá notificações no sistema e por email sobre novas campanhas das instituições que você segue. Também pode verificar regularmente a seção "Campanhas Disponíveis" no seu Dashboard.',
    },
  ];

  // Filtragem das FAQs
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom>
            Perguntas Frequentes
          </Typography>
          <Typography variant="h6">
            Encontre respostas para suas dúvidas sobre o ZenKroll
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1, mb: 4 }}>
        {/* Barra de Pesquisa */}
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            mb: 4,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Pesquisar pergunta..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <IconButton type="button" sx={{ p: '10px' }}>
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* Categorias */}
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <Chip
                key={category.id}
                icon={<Icon />}
                label={category.label}
                onClick={() => setSelectedCategory(category.id)}
                color={selectedCategory === category.id ? 'primary' : 'default'}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                sx={{ m: 0.5 }}
              />
            );
          })}
        </Box>

        {/* Lista de FAQs */}
        <Box sx={{ mb: 4 }}>
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <QuestionAnswerIcon color="primary" />
                    <Typography>{faq.question}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Alert severity="info">
              Nenhuma pergunta encontrada para sua pesquisa. Tente outros termos ou entre em contato com nosso suporte.
            </Alert>
          )}
        </Box>

        {/* Seção "Não encontrou sua resposta?" */}
        <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Não encontrou sua resposta?
          </Typography>
          <Typography variant="body1" paragraph>
            Nossa equipe de suporte está pronta para ajudar você.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                startIcon={<ContactSupportIcon />}
                component={Link}
                to="/suporte"
              >
                Central de Suporte
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<QuestionAnswerIcon />}
                href="mailto:suporte@zenkroll.com"
              >
                Enviar Email
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Links Úteis */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Links Úteis
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                startIcon={<AssignmentIcon />}
                component={Link}
                to="/tutorial-inscricao"
                sx={{ justifyContent: 'flex-start' }}
              >
                Tutorial de Pré-inscrição
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                startIcon={<PaymentIcon />}
                component={Link}
                to="/pagamentos"
                sx={{ justifyContent: 'flex-start' }}
              >
                Guia de Pagamentos
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                startIcon={<DateRangeIcon />}
                component={Link}
                to="/calendario"
                sx={{ justifyContent: 'flex-start' }}
              >
                Calendário de Campanhas
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                startIcon={<SchoolIcon />}
                component={Link}
                to="/instituicoes"
                sx={{ justifyContent: 'flex-start' }}
              >
                Lista de Instituições
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default FAQPage;